import { flexRender, type RowData } from "@tanstack/react-table";
import type {
    ViewTableCell,
    ViewTableCellContext,
    ViewTableRow,
} from "./core/table";
import type { DraggableSyntheticListeners } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import { memo, useMemo, CSSProperties } from "react";
// oxlint-disable-next-line no-restricted-imports -- ViewTable owns the GridTable implementation.
import { GridTableRow } from "@design-system/Tables/GridTable/GridTable";
import {
    ColumnPinData,
    hasGroupBoundary,
    isSamePinData,
    RowDropPosition,
} from "./ViewTable.lib";
import { getPinClasses } from "./getPinClasses";
import styles from "./ViewTable.module.scss";

export interface RowDragHandle {
    attributes: Record<string, unknown>;
    listeners: Record<string, unknown>;
    isDragging: boolean;
}

/**
 * The context `ViewTableRowComponent` renders each cell with. `canSelect` is
 * read fresh by the parent table: TanStack keeps `row` stable when only the
 * table options change, so a compiled cell can't rely on `row.getCanSelect()`.
 */
export interface ViewTableRowCellContext<
    TData extends RowData,
    TValue,
> extends ViewTableCellContext<TData, TValue> {
    tableMeta?: object;
    canSelect: boolean;
}

export interface CellContextWithDragHandle<
    TData extends RowData,
    TValue,
> extends ViewTableCellContext<TData, TValue> {
    rowDragHandle?: RowDragHandle;
}

const EMPTY_DND_PROPS = {};

export interface ViewTableRowComponentProps<
    TData extends { id: string | number },
> {
    row: ViewTableRow<TData>;
    index: number;
    columnPinningMap: Map<string, ColumnPinData>;
    tableMeta?: object;
    getRowProps: (
        row: ViewTableRow<TData>,
        index: number,
        state: {
            isActive: boolean;
            isFocused: boolean;
            isBulkSelected: boolean;
            isLastVisibleRow: boolean;
            isLastSubRow: boolean;
            isSectionStart: boolean;
        },
    ) => Record<string, any>;
    clickable?: boolean;
    isActive: boolean;
    isFocused: boolean;
    isBulkSelected: boolean;
    // Used only in the memo comparator below.
    isSelected: boolean;
    // Aggregate selection of descendants — used only in the comparator, so a
    // group/parent row re-renders (and its checkbox re-evaluates its dash /
    // check state) when a child's selection changes even though the parent's
    // own selection didn't.
    isSomeSubRowsSelected: boolean;
    isAllSubRowsSelected: boolean;
    isExpanded: boolean;
    canSelect: boolean;
    visibleCells: Array<ViewTableCell<TData, unknown>>;
    isLastVisibleRow: boolean;
    isLastSubRow: boolean;
    isSectionStart: boolean;
    // When set, this row is the current drag-over target: render an insertion
    // line above ("before") / below ("after") or a nest highlight ("on").
    dropIndicatorPosition?: RowDropPosition;
    // When true, the hovered drop is invalid (blocked by the consumer's
    // `validateDrop`): the indicator is recoloured to signal it won't land.
    dropIndicatorBlocked?: boolean;
    // When true, this row is part of the active drag's moving set (a
    // multi-selection or a group + descendants) but is not necessarily the
    // native active row: it gets the lifted "being dragged" treatment so the
    // whole moving set reads as picked up.
    isInActiveDragSet?: boolean;
    sortable?: {
        setNodeRef: (node: HTMLElement | null) => void;
        attributes: Record<string, unknown>;
        listeners: Record<string, unknown>;
        dragHandleAttributes: Record<string, unknown>;
        dragHandleListeners: Record<string, unknown>;
        dragHandleColumnId?: string;
        style: CSSProperties;
        isDragging: boolean;
        isDragHandleOnly: boolean;
    };
    // Drags the whole row out of the table (see `rowDragOut`). Never set
    // together with `sortable`: reordering turns drag-out off.
    dragOut?: {
        setNodeRef: (node: HTMLElement | null) => void;
        listeners: DraggableSyntheticListeners;
    };
}

function ViewTableRowInner<TData extends { id: string | number }>({
    row,
    index,
    columnPinningMap,
    tableMeta,
    getRowProps,
    clickable,
    isActive,
    isFocused,
    isBulkSelected,
    canSelect,
    isLastVisibleRow,
    isLastSubRow,
    isSectionStart,
    dropIndicatorPosition,
    dropIndicatorBlocked,
    isInActiveDragSet,
    sortable,
    dragOut,
}: ViewTableRowComponentProps<TData>) {
    const rowProps = getRowProps(row, index, {
        isActive,
        isFocused,
        isBulkSelected,
        isLastVisibleRow,
        isLastSubRow,
        isSectionStart,
    });

    return (
        <GridTableRow
            setNodeRef={sortable?.setNodeRef ?? dragOut?.setNodeRef}
            clickable={clickable}
            {...(sortable?.isDragHandleOnly ? {} : sortable?.attributes)}
            {...(sortable?.isDragHandleOnly ? {} : sortable?.listeners)}
            {...(sortable ? {} : dragOut?.listeners)}
            {...rowProps}
            className={classNames(rowProps.className, {
                [styles.draggingRow]: sortable?.isDragging,
                [styles.dragSetRow]: isInActiveDragSet,
                [styles.dropBefore]: dropIndicatorPosition === "before",
                [styles.dropOn]: dropIndicatorPosition === "on",
                [styles.dropAfter]: dropIndicatorPosition === "after",
                [styles.dropBlocked]:
                    !!dropIndicatorPosition && dropIndicatorBlocked,
            })}
            style={{
                ...(rowProps.style as CSSProperties),
                ...(sortable?.style ?? {}),
            }}
        >
            {row.getVisibleCells().map((cell) => {
                const pin = columnPinningMap.get(cell.column.id);
                const isDragHandleCell =
                    sortable?.isDragHandleOnly &&
                    sortable.dragHandleColumnId === cell.column.id;
                const cellContext = {
                    ...cell.getContext(),
                    tableMeta,
                    canSelect,
                } satisfies ViewTableRowCellContext<TData, unknown>;
                return (
                    <div
                        key={cell.id}
                        className={classNames(cell.column.id, styles.cell, {
                            ...getPinClasses(pin),
                            [styles.selectColumn]: cell.column.id === "select",
                            [styles.alignRight]:
                                cell.column.columnDef.meta?.align === "right",
                            [styles.groupBoundary]: hasGroupBoundary(
                                cell.column.columnDef.meta,
                            ),
                        })}
                        style={pin?.style}
                    >
                        {flexRender(
                            cell.column.columnDef.cell,
                            isDragHandleCell
                                ? ({
                                      ...cellContext,
                                      rowDragHandle: {
                                          attributes:
                                              sortable?.dragHandleAttributes ??
                                              EMPTY_DND_PROPS,
                                          listeners:
                                              sortable?.dragHandleListeners ??
                                              EMPTY_DND_PROPS,
                                          isDragging:
                                              sortable?.isDragging ?? false,
                                      },
                                  } satisfies CellContextWithDragHandle<
                                      TData,
                                      unknown
                                  >)
                                : cellContext,
                        )}
                    </div>
                );
            })}
        </GridTableRow>
    );
}

export const ViewTableRowComponent = memo(
    ViewTableRowInner,
    // Custom comparator: TanStack Table recreates Row objects on every
    // useTable recalculation even when the underlying data is
    // unchanged. The default shallow compare on `row` always fails
    // because it's a new object. We compare row.original (stable data
    // ref), row selection/expansion state, and per-row visual booleans.
    (prev, next) => {
        const prevVisibleCells = prev.visibleCells;
        const nextVisibleCells = next.visibleCells;
        const hasSameVisibleCells =
            prevVisibleCells.length === nextVisibleCells.length &&
            prevVisibleCells.every(
                (prevCell, index) =>
                    prevCell.id === nextVisibleCells[index]?.id &&
                    prevCell.column.columnDef ===
                        nextVisibleCells[index]?.column.columnDef,
            );

        return (
            prev.row.original === next.row.original &&
            prev.isSelected === next.isSelected &&
            prev.isSomeSubRowsSelected === next.isSomeSubRowsSelected &&
            prev.isAllSubRowsSelected === next.isAllSubRowsSelected &&
            prev.isExpanded === next.isExpanded &&
            prev.canSelect === next.canSelect &&
            prev.index === next.index &&
            nextVisibleCells.every(({ column }) =>
                isSamePinData(
                    prev.columnPinningMap.get(column.id),
                    next.columnPinningMap.get(column.id),
                ),
            ) &&
            prev.tableMeta === next.tableMeta &&
            prev.getRowProps === next.getRowProps &&
            prev.clickable === next.clickable &&
            prev.isActive === next.isActive &&
            prev.isFocused === next.isFocused &&
            prev.isBulkSelected === next.isBulkSelected &&
            prev.isLastVisibleRow === next.isLastVisibleRow &&
            prev.isLastSubRow === next.isLastSubRow &&
            prev.isSectionStart === next.isSectionStart &&
            prev.dropIndicatorPosition === next.dropIndicatorPosition &&
            prev.dropIndicatorBlocked === next.dropIndicatorBlocked &&
            prev.isInActiveDragSet === next.isInActiveDragSet &&
            prev.sortable?.isDragging === next.sortable?.isDragging &&
            prev.sortable?.style === next.sortable?.style &&
            prev.dragOut?.listeners === next.dragOut?.listeners &&
            hasSameVisibleCells
        );
    },
) as typeof ViewTableRowInner;

interface SortableViewTableRowProps<
    TData extends { id: string | number },
> extends ViewTableRowComponentProps<TData> {
    sortableId: string;
    isDragDisabled: boolean;
    dragHandleColumnId?: string;
}

function SortableViewTableRowInner<TData extends { id: string | number }>({
    sortableId,
    isDragDisabled,
    dragHandleColumnId,
    ...rowProps
}: SortableViewTableRowProps<TData>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: sortableId,
        disabled: isDragDisabled,
    });

    const sortableStyle = useMemo(() => {
        const normalizedTransform = transform
            ? {
                  ...transform,
                  x: 0,
                  scaleX: 1,
              }
            : null;

        return {
            transform: CSS.Transform.toString(normalizedTransform),
            transition,
        };
    }, [transform, transition]);

    const isDragHandleOnly = !!dragHandleColumnId;

    return (
        <ViewTableRowComponent
            {...rowProps}
            sortable={{
                setNodeRef,
                style: sortableStyle,
                isDragging,
                isDragHandleOnly,
                dragHandleColumnId,
                attributes:
                    !isDragDisabled && !isDragHandleOnly
                        ? (attributes ?? EMPTY_DND_PROPS)
                        : EMPTY_DND_PROPS,
                listeners:
                    !isDragDisabled && !isDragHandleOnly
                        ? (listeners ?? EMPTY_DND_PROPS)
                        : EMPTY_DND_PROPS,
                dragHandleAttributes:
                    !isDragDisabled && isDragHandleOnly
                        ? (attributes ?? EMPTY_DND_PROPS)
                        : EMPTY_DND_PROPS,
                dragHandleListeners:
                    !isDragDisabled && isDragHandleOnly
                        ? (listeners ?? EMPTY_DND_PROPS)
                        : EMPTY_DND_PROPS,
            }}
        />
    );
}

export const SortableViewTableRow = memo(
    SortableViewTableRowInner,
) as typeof SortableViewTableRowInner;
